// Shoreline + off-limits geometry. The hider can only be in an eligible station
// zone on Manhattan land and outside any off-limits area, so:
//   - zones are clipped to the Manhattan landmass
//   - a station is "blocked" if its center is off-land or inside an off-limits area
//   - the grey mask = land minus the union of eligible (active) clipped zones
import * as turf from "@turf/turf";
import { STATIONS, STATION_INDEX, ZONE_RADIUS_MILES } from "./stations";
import { setZoneRadius } from "./elimination";
import type { Station } from "./types";
import manhattanData from "../public/data/manhattan.json";
import blockZoneData from "../public/data/blockzones.json";

type Poly = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
interface OffLimit { id: string; name: string; geometry: GeoJSON.Geometry }

const LAND = manhattanData as unknown as Poly;

// Per-station block-zone geometries (baked from the OSM street grid).
const BLOCK_ZONES = blockZoneData as unknown as Record<string, Record<string, GeoJSON.Geometry>>;
const MODE_KEY: Record<string, string> = { block1: "b1", block2: "b2", block3: "b3" };
// Circle modes: zone = a plain radius (miles) around the station center.
const CIRCLE_RADII: Record<string, number> = { quarter: ZONE_RADIUS_MILES, sixth: 1 / 6, eighth: 1 / 8 };
// Effective zone radius (miles) per mode — drives the elimination buffer.
const ZONE_R: Record<string, number> = { ...CIRCLE_RADII, block1: 0.045, block2: 0.085, block3: 0.125 };

let zoneMode = "quarter";
/** Switch the hiding-zone geometry used everywhere (set from game config). */
export function setZoneMode(mode: string) {
  const m = ZONE_R[mode] !== undefined ? mode : "quarter";
  if (m === zoneMode) return;
  zoneMode = m;
  setZoneRadius(ZONE_R[m]);
  offSig = " "; // force the clip cache to rebuild under the new geometry
}

// Gamemaster per-station manual zone overrides, keyed by `${mode}|${stationId}`.
let overrides: Record<string, GeoJSON.Geometry> = {};
let overridesV = -1;
export function setZoneOverrides(map: Record<string, GeoJSON.Geometry>, version: number) {
  if (version === overridesV) return;
  overridesV = version;
  overrides = map || {};
  offSig = " "; // force rebuild — an override changed
}
export function overrideKey(mode: string, stationId: string) { return `${mode}|${stationId}`; }
export function currentZoneMode() { return zoneMode; }

function circle(st: Station, r: number): Poly {
  return turf.circle([st.lng, st.lat], r, { units: "miles", steps: 64 }) as Poly;
}

/** The hiding-zone polygon for a station under the current mode (override wins). */
function zoneFor(st: Station): Poly {
  const ov = overrides[overrideKey(zoneMode, st.id)];
  if (ov) return turf.feature(ov) as Poly;
  const r = CIRCLE_RADII[zoneMode];
  if (r !== undefined) return circle(st, r);
  const g = BLOCK_ZONES[st.id]?.[MODE_KEY[zoneMode]];
  return g ? (turf.feature(g) as Poly) : circle(st, ZONE_RADIUS_MILES);
}

/** The baked (un-overridden) zone geometry for a station — seeds the editor. */
export function bakedZoneGeometry(stationId: string): GeoJSON.Geometry {
  const st = STATIONS.find((s) => s.id === stationId)!;
  const r = CIRCLE_RADII[zoneMode];
  if (r !== undefined) return circle(st, r).geometry;
  return BLOCK_ZONES[st.id]?.[MODE_KEY[zoneMode]] ?? circle(st, ZONE_RADIUS_MILES).geometry;
}

// Stations whose center sits on Manhattan land (one-time).
const ON_LAND = new Set<string>(
  STATIONS.filter((s) => turf.booleanPointInPolygon([s.lng, s.lat], LAND)).map((s) => s.id)
);

function sig(off: OffLimit[]): string {
  return off.map((o) => o.id).join("|");
}

// --- cached derivations keyed by the off-limits signature ----------------
let offSig = " ";
let offUnion: Poly | null = null;
let clippedById = new Map<string, Poly>();

// An off-limits area MUST be a polygon for the turf clip/union/difference math.
// Geocoders return Points (e.g. "Marble Hill") and LineStrings for some places,
// which used to crash the mask build and wash the whole map grey — so normalize
// any geometry to a Polygon: buffer points/lines into a small area, keep polygons
// as-is, drop anything unusable.
function asPolygon(geom: GeoJSON.Geometry | undefined | null): Poly | null {
  if (!geom) return null;
  try {
    if (geom.type === "Polygon" || geom.type === "MultiPolygon") return turf.feature(geom) as Poly;
    if (geom.type === "Point" || geom.type === "MultiPoint") return turf.buffer(turf.feature(geom), 0.15, { units: "miles" }) as Poly;
    if (geom.type === "LineString" || geom.type === "MultiLineString") return turf.buffer(turf.feature(geom), 0.08, { units: "miles" }) as Poly;
  } catch { /* fall through */ }
  return null;
}
// Off-limits as normalized polygon features (used everywhere). Never throws.
function offPolys(off: OffLimit[]): Poly[] {
  return off.map((o) => asPolygon(o.geometry)).filter((p): p is Poly => p != null);
}
function safeUnion(feats: Poly[]): Poly | null {
  if (!feats.length) return null;
  if (feats.length === 1) return feats[0];
  try { return turf.union(turf.featureCollection(feats)) as Poly | null; } catch { return null; }
}
// An empty mask — renders nothing. Used as the safe fallback so a geometry failure
// dims NOTHING rather than the whole map.
const EMPTY_MASK: GeoJSON.Feature = { type: "Feature", geometry: { type: "MultiPolygon", coordinates: [] }, properties: {} };

function rebuild(off: OffLimit[]) {
  const s = zoneMode + "::" + sig(off);
  if (s === offSig) return;
  offSig = s;

  offUnion = off.length ? safeUnion(offPolys(off)) : null;

  // Clip each zone to land minus off-limits (depends only on off-limits). Each
  // station is wrapped so one bad geometry can't break the whole board.
  clippedById = new Map();
  for (const st of STATIONS) {
    try {
      const zone = zoneFor(st);
      let clipped = turf.intersect(turf.featureCollection([zone, LAND])) as Poly | null;
      if (clipped && offUnion) {
        try { clipped = turf.difference(turf.featureCollection([clipped, offUnion])) as Poly | null; } catch { /* keep the land-clipped zone */ }
      }
      if (clipped) clippedById.set(st.id, clipped);
    } catch { /* skip a station whose geometry can't be clipped */ }
  }
}

export function isBlocked(id: string, off: OffLimit[]): boolean {
  rebuild(off);
  const st = STATIONS.find((s) => s.id === id);
  if (!st) return true;
  if (!ON_LAND.has(id)) return true;
  return offUnion ? turf.booleanPointInPolygon([st.lng, st.lat], offUnion) : false;
}

/** Clipped zone polygons (to shoreline + off-limits) for rendering. */
export function clippedZoneFC(off: OffLimit[]): GeoJSON.FeatureCollection {
  rebuild(off);
  const features: GeoJSON.Feature[] = [];
  for (const st of STATIONS) {
    const c = clippedById.get(st.id);
    if (!c) continue;
    c.id = STATION_INDEX[st.id];
    c.properties = { id: st.id, name: st.name };
    features.push(c);
  }
  return { type: "FeatureCollection", features };
}

/** Off-limits polygons as a FeatureCollection (for the orange overlay). Points/
 * lines are buffered to areas so they actually show up. */
export function offLimitsFC(off: OffLimit[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const o of off) {
    const p = asPolygon(o.geometry);
    if (p) features.push({ type: "Feature", geometry: p.geometry, properties: { id: o.id, name: o.name } });
  }
  return { type: "FeatureCollection", features };
}

/** The grey mask: Manhattan land with the eligible zones cut out. */
export function buildMask(eligibleIds: string[], off: OffLimit[]): GeoJSON.Feature {
  rebuild(off);
  const zones = eligibleIds.map((id) => clippedById.get(id)).filter(Boolean) as Poly[];
  if (!zones.length) return LAND; // genuinely nothing eligible → dim all land (correct)
  const union = safeUnion(zones);
  // If the union/diff math fails (a bad geometry), dim NOTHING rather than the
  // whole map — failing open keeps the zones visible.
  if (!union) return EMPTY_MASK;
  let diff: GeoJSON.Feature | null = null;
  try { diff = turf.difference(turf.featureCollection([LAND, union])) as GeoJSON.Feature | null; } catch { diff = null; }
  return diff ?? EMPTY_MASK;
}

export { ZONE_RADIUS_MILES, type Station };
